import { getRanaApiV1 } from "@nens/rana/api-v1";

import { useMutationWithErrorHandler } from "../../hooks/use-mutation-with-error-handler";

type ClearlySubscriptionUser = {
  givenName: string;
  familyName: string;
  email: string;
};

type UseClearlySubscriptionProps = {
  identityProvider: string;
  user: ClearlySubscriptionUser | undefined;
};

const useRedirectOrPostClearlySubscription = (user: ClearlySubscriptionUser | undefined) => {
  return useMutationWithErrorHandler(async () => {
    if (!user) {
      console.warn("No user information available for Clearly subscription.");
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const payload = urlParams.get("data");
    const signature = urlParams.get("signature");

    // If there is a payload, it means the user has been redirected back from the Clearly Hub after subscribing,
    // so we can directly post the subscription to our backend.
    if (payload && signature) {
      await getRanaApiV1().ClearlyHubIntegrationApi.clearlyHubProcessSubscription({
        subscriptionRequest: {
          ...user,
          subscription: { payload, signature },
        },
      });

      // After processing the subscription, we can clean up the URL by removing the query parameters.
      // This will also make sure we refetch everything and re-PUT the user
      window.location.replace(window.location.pathname);
      return;
    }

    // If there is no payload, we need to redirect the user to the Clearly Hub subscription page.
    const redirectUrl = (
      await getRanaApiV1().ClearlyHubIntegrationApi.clearlyHubSubscriptionRedirect({
        next: window.location.href,
      })
    ).url;
    window.location.href = redirectUrl;
  });
};

const CLEARLY_HUB_IDP_KEY = "ClearlyHub2";

export type ClearlyHookReturnIsClearly = {
  isClearly: true;
  redirectOrPostToClearly: () => void;
  errors: ReturnType<typeof useRedirectOrPostClearlySubscription>["errors"];
};

type ClearlyHookReturnNotClearly = {
  isClearly: false;
};

export type UseClearlySubscriptionReturn = ClearlyHookReturnIsClearly | ClearlyHookReturnNotClearly;

export const useClearlySubscription = ({
  identityProvider,
  user,
}: UseClearlySubscriptionProps): UseClearlySubscriptionReturn => {
  const { mutate: redirectOrPostToClearly, errors } = useRedirectOrPostClearlySubscription(user);
  if (identityProvider !== CLEARLY_HUB_IDP_KEY) return { isClearly: false };

  return { isClearly: true, redirectOrPostToClearly, errors };
};
